// Company and legal pages: /company/:slug
// Legal pages are plain-English drafts for review by a lawyer before launch. Items in [square brackets]
// (address, officer name, emails) must be filled in. Forms post to /v1/contact and /v1/creators/apply in production.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { getCatalog, useCatalog } from '../lib/catalog';
import { Link } from '../lib/router';
import { api } from '../lib/api';
import { toast } from '../lib/store';
import { Icon } from '../components/Icon';
import { AppBar, COMPANY_PAGES, PublicFooter, PublicHeader, SocialIcons, StoreButtons } from '../components/PublicChrome';

const UPDATED = '17 October 2026';

function Draft() {
  return <p className="legal-draft"><Icon name="shield" size={16} /> Draft for legal review. Text in [square brackets] must be filled in before launch.</p>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <section className="co-section"><h2>{title}</h2>{children}</section>;
}

function ContactForm({ kind }: { kind: 'contact' | 'creator' }) {
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const phone = String(f.get('phone') || '').replace(/\D/g, '').slice(-10);
    const name = String(f.get('name') || '').trim();
    const message = String(f.get('message') || '').trim();
    if (name.length < 2) { setErr('Enter your name.'); return; }
    if (!/^[6-9]\d{9}$/.test(phone)) { setErr('Enter a 10-digit Indian mobile number.'); return; }
    if (kind === 'contact' && message.length < 5) { setErr('Write a short message so we know how to help.'); return; }
    setErr(null);
    setBusy(true);
    try {
      await api.submitForm(kind, { name, phone, topic: String(f.get('topic') || ''), ...(kind === 'creator' ? { link: String(f.get('link') || '').trim() } : { message }) });
      setSent(true);
      toast(kind === 'creator' ? 'Application sent. We will call you within 7 days.' : 'Message sent. We will reply within 2 working days.');
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return <div className="co-sent"><span className="done-check sm"><Icon name="check" size={22} /></span><b>Thank you.</b><p className="muted">{kind === 'creator' ? 'Our creator team will call you within 7 days.' : 'We will reply within 2 working days.'}</p></div>;
  }
  return (
    <form className="co-form" onSubmit={submit} noValidate>
      <label className="label" htmlFor={`${kind}-name`}>Your name</label>
      <input id={`${kind}-name`} name="name" className="input" autoComplete="name" placeholder="Priya Sharma" />
      <label className="label" htmlFor={`${kind}-phone`}>Mobile number</label>
      <input id={`${kind}-phone`} name="phone" className="input" inputMode="numeric" autoComplete="tel" placeholder="98765 43210" />
      {kind === 'creator' ? (
        <>
          <label className="label" htmlFor="creator-topic">What kind of shows will you make?</label>
          <select id="creator-topic" name="topic" className="input">{getCatalog().topics.map((t) => <option key={t.slug}>{t.name}</option>)}<option>Something else</option></select>
          <label className="label" htmlFor="creator-link">Link to your videos (YouTube, Instagram or Drive)</label>
          <input id="creator-link" name="link" className="input" inputMode="url" placeholder="https://" />
        </>
      ) : (
        <>
          <label className="label" htmlFor="contact-topic">What is it about?</label>
          <select id="contact-topic" name="topic" className="input"><option>Help with the app</option><option>Payment or AutoPay</option><option>Business or partnership</option><option>Press</option><option>Something else</option></select>
          <label className="label" htmlFor="contact-msg">Message</label>
          <textarea id="contact-msg" name="message" className="input" rows={4} placeholder="Tell us how we can help" />
        </>
      )}
      {err && <p className="field-error" role="alert">{err}</p>}
      <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Sending…' : kind === 'creator' ? 'Apply as a creator' : 'Send message'}</button>
    </form>
  );
}

const PAGES: Record<string, { title: string; lead: string; body: () => ReactNode }> = {
  about: {
    title: 'About Kuch Naya',
    lead: 'We make short shows for every home in Bharat: micro dramas, comedy and college stories that feel real, and how-tos that make life easier.',
    body: () => (
      <>
        <ul className="co-stats">
          <li><b>{getCatalog().shows.length}</b><span>shows</span></li><li><b>{getCatalog().episodes.length}</b><span>episodes</span></li><li><b>{getCatalog().topics.length}</b><span>topics</span></li>
        </ul>
        <Section title="Why we started">
          <p>People want stories that look like their own lives, a good laugh after work, and quick help with things like a PAN card, a SIP or a first interview. Most of what is out there is either long, confusing or not made for them. Kuch Naya puts all of it in one place as short shows of 2 to 10 minute episodes you can watch on any phone. Dekho kuch naya har roz.</p>
        </Section>
        <Section title="What we believe">
          <ul className="co-list">
            <li><b>Relatable first.</b> Stories about real families, friends, hostels and first jobs.</li>
            <li><b>Simple beats clever.</b> Everyday words, and every how-to shows each step.</li>
            <li><b>Trust first.</b> Creators are checked, facts are reviewed, and we never promise quick riches.</li>
            <li><b>Respect people’s time and data.</b> Short episodes, a Data saver mode and offline downloads.</li>
          </ul>
        </Section>
        <Section title="Who we are">
          <p>Kuch Naya is built by Paper2Pixel, a product studio based in India. Our team of writers, directors, editors, researchers and engineers works with creators across the country.</p>
          <div className="row-wrap"><Link to="/company/careers" className="btn btn-ghost">Work with us</Link><Link to="/company/creators" className="btn btn-ghost">Become a creator</Link></div>
        </Section>
      </>
    ),
  },
  creators: {
    title: 'Become a Kuch Naya creator',
    lead: 'Writers, actors, comedians and experts: make short shows on Kuch Naya and reach viewers across India.',
    body: () => (
      <div className="co-two">
        <div>
          <Section title="Who we are looking for">
            <ul className="co-list">
              <li>Storytellers, actors and comedians for micro dramas, college stories and comedy sketches.</li>
              <li>People with real experience in a topic: government work, money, jobs, English, business, earning online and more.</li>
              <li>Clear speakers in Hindi, English or a regional language.</li>
              <li>No studio needed. A phone, good light and a quiet room are enough.</li>
            </ul>
          </Section>
          <Section title="How it works">
            <ol className="co-steps">
              <li><b>Apply</b><span>Share a link to any video you have made.</span></li>
              <li><b>Talk to our team</b><span>We call you within 7 days to plan a show.</span></li>
              <li><b>Make your show</b><span>Our writers, directors and editors help with the script, shoot and edit.</span></li>
              <li><b>Go live and earn</b><span>Your show reaches viewers in the app. You earn from it as set out in your creator agreement.</span></li>
            </ol>
          </Section>
        </div>
        <div className="co-card"><h2>Apply now</h2><ContactForm kind="creator" /></div>
      </div>
    ),
  },
  careers: {
    title: 'Careers at Kuch Naya',
    lead: 'Help build Bharat’s home for short shows that entertain and help. We hire people who care about stories, craft and the people we make this for.',
    body: () => (
      <>
        <Section title="How we work">
          <ul className="co-list">
            <li><b>Viewer first.</b> We test with real viewers in small towns before we ship.</li>
            <li><b>Small teams, real ownership.</b> You see your work in people’s hands quickly.</li>
            <li><b>Hindi and English, both first-class.</b> Our team writes and reviews in both.</li>
          </ul>
        </Section>
        <Section title="Open roles">
          <p className="muted small">Example roles for the preview. Edit this list in <code>src/pages/Company.tsx</code>.</p>
          <ul className="co-roles">
            {[['Micro drama writer (Hindi)', 'Content', 'Remote'], ['Director, short-form drama', 'Content', 'Hybrid'], ['Video editor (short form)', 'Content', 'Remote'], ['Creator partnerships lead', 'Creators', 'Hybrid'], ['Android engineer', 'Engineering', 'Hybrid'], ['Backend engineer (Cloudflare Workers)', 'Engineering', 'Remote']].map(([r, team, where]) => (
              <li key={r}><span><b>{r}</b><small className="muted">{team} · {where}</small></span><Link to="/company/contact" className="btn btn-sm btn-ghost">Apply</Link></li>
            ))}
          </ul>
          <p>Do not see your role? Write to us at <b>[careers email]</b> with what you would love to work on.</p>
        </Section>
      </>
    ),
  },
  contact: {
    title: 'Contact us',
    lead: 'We are here to help. Most questions are answered fastest inside the app under Account › Help.',
    body: () => (
      <div className="co-two">
        <div>
          <Section title="Ways to reach us">
            <ul className="co-contacts">
              <li><Icon name="whatsapp" size={20} /><span><b>WhatsApp support</b><small className="muted">[WhatsApp number] · 9 am to 9 pm, every day</small></span></li>
              <li><Icon name="bell" size={20} /><span><b>Email</b><small className="muted">[support email] · reply within 2 working days</small></span></li>
              <li><Icon name="rupee" size={20} /><span><b>Payments and AutoPay</b><small className="muted">Account › Manage AutoPay in the app, or write to [support email]</small></span></li>
              <li><Icon name="user" size={20} /><span><b>Creators and partnerships</b><small className="muted">[partners email]</small></span></li>
              <li><Icon name="home" size={20} /><span><b>Office</b><small className="muted">Paper2Pixel, [registered office address]</small></span></li>
            </ul>
            <p>For complaints about content or your data, contact our <Link to="/company/grievance">Grievance officer</Link>.</p>
          </Section>
          <Section title="Follow us"><SocialIcons /></Section>
        </div>
        <div className="co-card"><h2>Send a message</h2><ContactForm kind="contact" /></div>
      </div>
    ),
  },
  privacy: {
    title: 'Privacy policy',
    lead: 'This policy explains what we collect, why, and the choices you have. We follow India’s Digital Personal Data Protection Act, 2023.',
    body: () => (
      <>
        <Draft />
        <Section title="What we collect">
          <ul className="co-list">
            <li><b>Account:</b> your mobile number, and your name if you add it.</li>
            <li><b>Use of the app:</b> what you watch, how far, saves, likes and settings, so you can continue where you left off and get better suggestions.</li>
            <li><b>Device:</b> device type, app version and approximate location from your network, to keep the service working and secure.</li>
            <li><b>Payments:</b> whether your Plus membership is active and your AutoPay status. Your UPI PIN and bank details are handled by our payment partner and are never seen or stored by us.</li>
          </ul>
        </Section>
        <Section title="Why we use it">
          <p>To run your account, play videos, process Plus payments, send the reminders the law requires before each AutoPay payment, prevent piracy and fraud, and improve the app. We do not sell your personal data.</p>
        </Section>
        <Section title="Who we share it with">
          <p>Only with service providers that help us run Kuch Naya, such as our payment partner (for UPI AutoPay), our cloud and video delivery provider, and SMS or WhatsApp providers for OTPs and reminders. They may use the data only to provide their service to us. We share data with authorities only when the law requires it.</p>
        </Section>
        <Section title="How long we keep it">
          <p>We keep your data while your account is active. If you delete your account, we delete or anonymise your personal data within [30] days, except records we must keep by law, such as payment records.</p>
        </Section>
        <Section title="Your rights">
          <p>You can see, correct or delete your data, withdraw consent, and nominate someone to act for you. Delete your account any time from Account › Delete account, or contact our <Link to="/company/grievance">Grievance officer</Link>. If you are not satisfied, you may complain to the Data Protection Board of India.</p>
        </Section>
        <Section title="Children">
          <p>Kuch Naya is meant for adults. If you are under 18, use it only with a parent or guardian’s consent. We do not show targeted ads to children or track them for advertising.</p>
        </Section>
        <p className="muted small">Last updated {UPDATED}. Paper2Pixel, [registered office address].</p>
      </>
    ),
  },
  terms: {
    title: 'Terms of use',
    lead: 'These terms apply when you use the Kuch Naya app or website. By using Kuch Naya you agree to them.',
    body: () => (
      <>
        <Draft />
        <Section title="Your account"><p>Sign up with your own mobile number and keep your OTP private. One account is for one person. You are responsible for activity on your account.</p></Section>
        <Section title="Kuch Naya Plus and UPI AutoPay">
          <p>Plus is a monthly membership. You see the price in the app before you pay. When you set up UPI AutoPay, you allow us to charge the amount shown each month until you cancel. We notify you at least 24 hours before each payment. You can cancel any time in the app or your UPI app; Plus stays active until the end of the period you paid for.</p>
        </Section>
        <Section title="Using the videos">
          <p>Videos are for your personal, non-commercial viewing. Do not download (except through the app’s offline feature), record, copy, share or re-upload them. Videos may carry a hidden or visible viewer code to help us trace leaks.</p>
        </Section>
        <Section title="Information, not professional advice">
          <p>Our how-to shows explain topics in simple terms. They are not legal, tax, medical or investment advice. Check official sources or a professional before making important decisions.</p>
        </Section>
        <Section title="Things you must not do"><p>Break the law, harass others, try to get around payments or protections, or use bots to access the service.</p></Section>
        <Section title="Changes and ending"><p>We may update these terms and will tell you about important changes. We may suspend accounts that break them. You can stop using Kuch Naya and delete your account at any time.</p></Section>
        <Section title="Law and disputes"><p>These terms are governed by the laws of India. Courts in [city] have jurisdiction.</p></Section>
        <p className="muted small">Last updated {UPDATED}.</p>
      </>
    ),
  },
  refunds: {
    title: 'Refund and cancellation',
    lead: 'You can cancel Kuch Naya Plus at any time. Here is what happens to your payments.',
    body: () => (
      <>
        <Draft />
        <Section title="Cancelling">
          <ul className="co-list">
            <li>In the app: Account › Manage AutoPay › Cancel.</li>
            <li>Or in your UPI app: stop the Kuch Naya AutoPay mandate.</li>
            <li>After you cancel, no more payments are taken. Plus stays active until the end of the month you already paid for.</li>
          </ul>
        </Section>
        <Section title="Refunds">
          <ul className="co-list">
            <li>We do not refund part of a month after it has started.</li>
            <li>If you were charged twice, charged after cancelling, or charged in error, we refund the full amount to the same UPI account within [5 to 7] working days of confirming it.</li>
            <li>If a payment failed but money left your account, it is usually returned by your bank automatically within [5] working days.</li>
          </ul>
        </Section>
        <Section title="How to ask for a refund"><p>Write to [support email] or use Account › Help in the app with your registered mobile number and the payment date. We reply within 2 working days.</p></Section>
        <p className="muted small">Last updated {UPDATED}.</p>
      </>
    ),
  },
  grievance: {
    title: 'Grievance officer',
    lead: 'If you have a complaint about content on Kuch Naya or about how we handle your personal data, contact our Grievance officer.',
    body: () => (
      <>
        <Draft />
        <div className="co-card co-officer">
          <dl className="kv">
            <div><dt>Name</dt><dd>[Grievance officer name]</dd></div>
            <div><dt>Email</dt><dd>[grievance email]</dd></div>
            <div><dt>Address</dt><dd>Paper2Pixel, [registered office address]</dd></div>
            <div><dt>Hours</dt><dd>Monday to Friday, 10 am to 6 pm</dd></div>
          </dl>
        </div>
        <Section title="What happens next">
          <ol className="co-steps">
            <li><b>We acknowledge your complaint</b><span>within 24 hours.</span></li>
            <li><b>We resolve it</b><span>within 15 days, as required by the IT Rules, 2021.</span></li>
            <li><b>Not satisfied?</b><span>For data complaints you may approach the Data Protection Board of India.</span></li>
          </ol>
        </Section>
        <Section title="Please include"><p>Your registered mobile number, a link or name of the show or episode (if it is about content), and a short description of the problem.</p></Section>
      </>
    ),
  },
};

export function CompanyPage({ slug }: { slug: string }) {
  useCatalog(); // re-render when the live catalogue arrives
  const page = PAGES[slug];
  useEffect(() => {
    document.title = page ? `${page.title} · Kuch Naya` : 'Kuch Naya';
    window.scrollTo(0, 0);
  }, [page]);
  const legal = COMPANY_PAGES.find((p) => p.slug === slug)?.group === 'legal';
  return (
    <div className="landing lp pub">
      <PublicHeader />
      {page ? (
        <>
          <nav className="wrap crumbs" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><span aria-current="page">{page.title}</span></nav>
          <section className={'wrap co-hero' + (legal ? ' legal' : '')}>
            <h1>{page.title}</h1>
            <p className="lp-sub">{page.lead}</p>
          </section>
          <div className={'wrap co-body' + (legal ? ' legal' : '')}>
            {legal && (
              <aside className="co-side" aria-label="Legal pages">
                {COMPANY_PAGES.filter((p) => p.group === 'legal').map((p) => (
                  <Link key={p.slug} to={`/company/${p.slug}`} className={p.slug === slug ? 'on' : ''} aria-current={p.slug === slug ? 'page' : undefined}>{p.title}</Link>
                ))}
              </aside>
            )}
            <article className="co-article">{page.body()}</article>
          </div>
          {!legal && (
            <section className="wrap l-section">
              <div className="lp-app"><div className="lp-app-copy"><h2>Get the Kuch Naya app</h2><p>Free to download. Dekho kuch naya har roz.</p><StoreButtons /></div></div>
            </section>
          )}
        </>
      ) : (
        <section className="wrap co-hero"><h1>Page not found</h1><p className="lp-sub">Go back to the <Link to="/">homepage</Link>.</p></section>
      )}
      <PublicFooter />
      <AppBar />
    </div>
  );
}
